import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { ServiceInstance } from "~/types/serviceInstance"
import { text } from "~/components/TextResources";
import { deploymentCloudServices, serviceInstanceService, radioService, edgeService } from "~/services";
import { PayloadLocationType, PayloadServiceInstanceStatus, Summary } from "~/types/summary";
import { ServiceInstanceRelease, RadioRelease, EdgeRelease } from "~/types/SoftwareReleases";
import React from "react";
import { DeploymentCloud } from "~/types/deploymentCLoud";
import { RadioManager } from "~/types/RadioManager";
import { NETWORK_TYPE } from "~/types/Edge";

export type TenantTree = {
  id: string;
  name: string;
  vmcCount: number;
  vmcHealthyCount: number;
  isPaused?: boolean;
  icon?: React.ReactNode;
  serviceType?: string[];
  children?: TenantTree[];
}

export interface ServiceInstanceFilters {
  selectedCloudFilters: string[];
  selectedVmcFilters: string[];
  selectedServiceTypeFilters: string[];
}

type initState = {
  open: boolean;
  loading: boolean;
  current: ServiceInstance | null;
  data: Array<ServiceInstance>;
  summary: Summary | null;
  profileOpen: boolean;
  filters: ServiceInstanceFilters;

  deploymentCloud: DeploymentCloud[];

  vmcReleases: ServiceInstanceRelease[];
  vmcReleasesLoading: boolean;
  radioReleases: RadioRelease[];
  radioReleasesLoading: boolean;
  edgeReleases: EdgeRelease[];
  edgeReleasesLoading: boolean;

  // Newest in-flight query per release list; an older, slower response must not
  // overwrite a newer one (MCS-4302)
  vmcReleasesRequestId: string | null;
  radioReleasesRequestId: string | null;
  edgeReleasesRequestId: string | null;

  // Which filter combination edgeReleases currently holds, so a consumer can tell
  // whether the list actually answers the question it is asking (MCS-4302)
  edgeReleasesKey: string | null;

  radioManager: {
    data: RadioManager[];
    current: RadioManager | null;
    open: boolean;
    loading: boolean;
  };
}

const initialState: initState = {
  loading: false,
  current: null,
  data: [],
  open: false,
  summary: null,
  profileOpen: false,
  filters: {
    selectedCloudFilters: [],
    selectedVmcFilters: [],
    selectedServiceTypeFilters: []
  },

  deploymentCloud: [],

  vmcReleases: [],
  vmcReleasesLoading: false,
  radioReleases: [],
  radioReleasesLoading: false,
  edgeReleases: [],
  edgeReleasesLoading: false,

  vmcReleasesRequestId: null,
  radioReleasesRequestId: null,
  edgeReleasesRequestId: null,
  edgeReleasesKey: null,

  radioManager: {
    data: [],
    current: null,
    open: false,
    loading: false,
  },
}
export const fetchServiceInstancesAndDeploymentCloud = createAsyncThunk(`serviceInstance/fetchServiceInstancesAndDeploymentCloud`, async () => {
  let serviceInstancesData: ServiceInstance[] = [];
  try {
    serviceInstancesData = (await serviceInstanceService.getServiceInstances()) ?? [];
  } catch (error) {
    console.error(error);
    throw error;
  }

  let deploymentCloudData: DeploymentCloud[] = [];

  try {
    const result = await deploymentCloudServices.getClusterAgents();
    deploymentCloudData = Array.isArray(result) ? result : [];
  } catch (error) {
    console.warn(
      "Failed to fetch deployment clouds; service instances will load without enriched deployment cloud data.",
      error,
    );
  }

  // Merge cloud details here on plain objects; skipped for tenant/partner
  // users (empty cloud list, no permission) or when the cloud call fails
  if (deploymentCloudData.length) {
    const cloudById = new Map(deploymentCloudData.map((cloud) => [cloud.id, cloud]));
    for (const instance of serviceInstancesData) {
      const cloud = cloudById.get(instance.deploymentCloud?.id);
      if (cloud) {
        instance.deploymentCloud = cloud;
      }
    }
  }

  return { serviceInstancesData, deploymentCloudData };
});
export const fetchServiceInstances = createAsyncThunk(`serviceInstance/fetchServiceInstance`, async () => {
  try {
    const data = await serviceInstanceService.getServiceInstances();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const fetchDeploymentCloud = createAsyncThunk(`serviceInstance/fetchDeploymentCloud`, async () => {
  try {
    const data = await deploymentCloudServices.getClusterAgents();
    return data ?? [];
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const fetchSaasSummary = createAsyncThunk(`serviceInstance/fetchSaasSummary`, async ({ locationType, serviceInstanceStatus, serviceType, tenantId }: { locationType: PayloadLocationType[], serviceInstanceStatus: PayloadServiceInstanceStatus[], serviceType?: string[], tenantId?: string }) => {
  try {
    const data = await serviceInstanceService.getSaasSummary({ locationType, serviceInstanceStatus, serviceType, tenantId });
    return data as Summary;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const fetchServiceInstanceReleases = createAsyncThunk(
  `serviceInstance/fetchServiceInstanceReleases`,
  async (serviceInstanceId: string, { rejectWithValue }) => {
    try {
      const data = await serviceInstanceService.getServiceInstanceReleases(serviceInstanceId);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error);
    }
  }
);

export const fetchVMCReleases = createAsyncThunk(
  `serviceInstance/fetchVMCReleases`,
  async (payload?: { filter: { tenantId?: string } }) => {
    try {
      const data = await serviceInstanceService.getVMCReleases(payload);
      return data
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
);

export const fetchRadioReleases = createAsyncThunk(
  `serviceInstance/fetchRadioReleases`,
  async (payload: any = { filter: {} }) => {
    try {
      const data = await radioService.getRadioReleases();
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
);

export type EdgeReleaseFilter = {
  networkType?: NETWORK_TYPE,
  edgeLocationType?: string,
  locationType?: string,
  tenantId?: string,
  vmcRelease?: string,
};

/** Identity of an edge release query - lets a consumer check the stored list was fetched for the filters it cares about (MCS-4302) */
export const edgeReleaseFilterKey = (filter?: EdgeReleaseFilter) => JSON.stringify([
  filter?.networkType ?? "",
  filter?.locationType ?? filter?.edgeLocationType ?? "",
  filter?.tenantId ?? "",
  filter?.vmcRelease ?? "",
]);

export const fetchEdgeReleases = createAsyncThunk(
  `serviceInstance/fetchEdgeReleases`,
  async (payload: { filter: EdgeReleaseFilter } | undefined, { signal }) => {
    try {
      const data = await edgeService.getEdgeReleases(payload, signal);
      return data;
    } catch (error) {
      // A superseded query is aborted on purpose - not worth reporting
      if (!signal.aborted) console.error(error);
      throw error;
    }
  }
);

export const updateRadioRelease = createAsyncThunk(
  `serviceInstance/updateRadioRelease`,
  async (payload: {
    id: string | undefined;
    release: string | undefined;
    releaseNotes: string;
    unsupported: boolean;
    isDefault: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await radioService.updateradioRelease(payload);
      const text = await response.text();
      const result = text ? JSON.parse(text) : null;
      if (!response.ok) {
        return rejectWithValue(result ?? { errors: [{ message: `Request failed with status ${response.status}` }], warnings: [] });
      }
      return result?.data ?? result;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error);
    }
  }
);


export const fetchRadioManagers = createAsyncThunk(
  `serviceInstance/fetchRadioManagers`,
  async () => {
    try {
      const data = await serviceInstanceService.getRadioManagers();
      return data as RadioManager[];
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
);


const serviceInstanceSlice = createSlice({
  name: text.serviceInstance as string,
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setProfileOpen: (state, action: PayloadAction<boolean>) => {
      state.profileOpen = action.payload;
    },
    setCloudFilters: (state, action: PayloadAction<string[]>) => {
      state.filters.selectedCloudFilters = action.payload;
    },
    setVmcFilters: (state, action: PayloadAction<string[]>) => {
      state.filters.selectedVmcFilters = action.payload;
    },
    setServiceTypeFilters: (state, action: PayloadAction<string[]>) => {
      state.filters.selectedServiceTypeFilters = action.payload;
    },
    setSelectedRadioManager: (state, action: PayloadAction<RadioManager | null>) => {
      state.radioManager.current = action.payload;
    },
    setRadioManagerDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.radioManager.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceInstances.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchServiceInstances.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchServiceInstances.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchServiceInstancesAndDeploymentCloud.pending, (state) => {
        state.loading = true;
        // Sync current with latest data
      })
      .addCase(fetchServiceInstancesAndDeploymentCloud.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchServiceInstancesAndDeploymentCloud.fulfilled, (state, action) => {
        state.loading = false;

        const { serviceInstancesData, deploymentCloudData } = action.payload;

        // Cloud details are merged in the thunk on plain objects - per-instance
        // draft writes with a linear cloud scan each ran O(VMCs x clouds) here
        state.data = serviceInstancesData ?? [];
        // Kept so consumers can tell a deleted deployment cloud from an unavailable cloud list (MCS-4170)
        state.deploymentCloud = deploymentCloudData;

        // Sync current with latest data
        if (state.current && !state.open) {
          const current = serviceInstancesData.find((item: any) => item.id === state.current?.id);
          if (current) {
            state.current = current;
          }
        }
      })
      .addCase(fetchSaasSummary.fulfilled, (state, action) => {
        state.summary = action.payload

        // Sync current with latest data
      })
      .addCase(fetchVMCReleases.pending, (state, action) => {
        state.vmcReleasesLoading = true;
        state.vmcReleasesRequestId = action.meta.requestId;
      })
      .addCase(fetchVMCReleases.fulfilled, (state, action) => {
        if (state.vmcReleasesRequestId !== action.meta.requestId) return;
        state.vmcReleasesLoading = false;
        state.vmcReleases = action.payload;
      })
      .addCase(fetchVMCReleases.rejected, (state, action) => {
        if (state.vmcReleasesRequestId !== action.meta.requestId) return;
        state.vmcReleasesLoading = false;
      })
      .addCase(fetchRadioReleases.pending, (state, action) => {
        state.radioReleasesLoading = true;
        state.radioReleasesRequestId = action.meta.requestId;
      })
      .addCase(fetchRadioReleases.fulfilled, (state, action) => {
        if (state.radioReleasesRequestId !== action.meta.requestId) return;
        state.radioReleasesLoading = false;
        state.radioReleases = action.payload;
      })
      .addCase(fetchRadioReleases.rejected, (state, action) => {
        if (state.radioReleasesRequestId !== action.meta.requestId) return;
        state.radioReleasesLoading = false;
      })
      .addCase(fetchEdgeReleases.pending, (state, action) => {
        state.edgeReleasesLoading = true;
        state.edgeReleasesRequestId = action.meta.requestId;
      })
      .addCase(fetchEdgeReleases.fulfilled, (state, action) => {
        if (state.edgeReleasesRequestId !== action.meta.requestId) return;
        state.edgeReleasesLoading = false;
        state.edgeReleases = action.payload;
        state.edgeReleasesKey = edgeReleaseFilterKey(action.meta.arg?.filter);
      })
      .addCase(fetchEdgeReleases.rejected, (state, action) => {
        if (state.edgeReleasesRequestId !== action.meta.requestId) return;
        state.edgeReleasesLoading = false;
      })
      .addCase(fetchRadioManagers.pending, (state) => {
        state.radioManager.loading = true;
      })
      .addCase(fetchRadioManagers.fulfilled, (state, action) => {
        state.radioManager.loading = false;
        state.radioManager.data = action.payload;
        // Sync current with latest data
        if (state.radioManager.current && !state.radioManager.open) {
          const current = action.payload.find((item) => item.id === state.radioManager.current?.id);
          if (current) {
            state.radioManager.current = current;
          }
        }
      })
      .addCase(fetchRadioManagers.rejected, (state) => {
        state.radioManager.loading = false;
      })
      .addCase(fetchDeploymentCloud.fulfilled, (state, action) => {
        state.deploymentCloud = action.payload;
      })
      .addCase(fetchDeploymentCloud.rejected, (state) => {
        state.deploymentCloud = [];
      })
  },
});

export const serviceInstanceActions = serviceInstanceSlice.actions;
export default serviceInstanceSlice.reducer;
export const serviceInstanceState = (state: RootState) => state.serviceInstance.current
export const serviceInstanceData = (state: RootState) => state.serviceInstance.data
export const serviceInstanceOpen = (state: RootState) => state.serviceInstance.open
export const serviceInstanceLoading = (state: RootState) => state.serviceInstance.loading;
// Lazy - built only when a consumer subscribes, instead of twice per 30s poll
export const tenantTree = createSelector([serviceInstanceData], getTenantTree);
export const saasSummary = (state: RootState) => state.serviceInstance.summary;
export const openProfileEdit = (state: RootState) => state.serviceInstance.profileOpen;
export const selectCloudFilters = (state: RootState) => state.serviceInstance.filters.selectedCloudFilters;
export const deploymentCloudData = (state: RootState) => state.serviceInstance.deploymentCloud;
export const selectVmcFilters = (state: RootState) => state.serviceInstance.filters.selectedVmcFilters;
export const selectServiceTypeFilters = (state: RootState) => state.serviceInstance.filters.selectedServiceTypeFilters;
export const vmcReleasesData = (state: RootState) => state.serviceInstance.vmcReleases;
export const vmcReleasesLoading = (state: RootState) => state.serviceInstance.vmcReleasesLoading;
export const radioReleasesData = (state: RootState) => state.serviceInstance.radioReleases;
export const radioReleasesLoading = (state: RootState) => state.serviceInstance.radioReleasesLoading;
export const edgeReleasesData = (state: RootState) => state.serviceInstance.edgeReleases;
export const edgeReleasesKey = (state: RootState) => state.serviceInstance.edgeReleasesKey;
export const edgeReleasesLoading = (state: RootState) => state.serviceInstance.edgeReleasesLoading;
export const radioManagersData = (state: RootState) => state.serviceInstance.radioManager.data;
export const radioManagersLoading = (state: RootState) => state.serviceInstance.radioManager.loading;
export const selectedRadioManager = (state: RootState) => state.serviceInstance.radioManager.current;
export const radioManagerDialogOpen = (state: RootState) => state.serviceInstance.radioManager.open;


export function getTenantTree(instances : ServiceInstance[]){
  const tenantTree: TenantTree = {
    id : "all",
    name : "All",
    vmcCount : 0,
    vmcHealthyCount : 0,
    children : []
  }
  const list = tenantTree.children ?? [];
  // Indexed - list.find per instance made this O(VMCs x tenants) per poll
  const tenantById = new Map<string | undefined, TenantTree>();

  instances.forEach((instance) => {
    const instanceTypes = instance.runtimeInfo?.serviceType ?? [];
    const tenant = tenantById.get(instance.tenantId);
    if (tenant) {
      tenant.vmcCount += 1;
      tenant.vmcHealthyCount += instance.runtimeInfo.monitorStatus?.health === "GOOD" ? 1 : 0;
      for (const t of instanceTypes) {
        if (t && !tenant.serviceType?.includes(t)) {
          tenant.serviceType = [...(tenant.serviceType ?? []), t];
        }
      }
    } else {
      const node: TenantTree = {
        id: instance.tenantId,
        name: instance.tenantName,
        vmcCount: 1,
        vmcHealthyCount: instance.runtimeInfo.monitorStatus?.health === "GOOD" ? 1 : 0,
        isPaused : instance.pauseStatus === "PAUSED",
        serviceType: instanceTypes.filter(Boolean),
      };
      list.push(node);
      tenantById.set(instance.tenantId, node);
  }
  });
  return tenantTree;
}
